import { showNotification } from '@mantine/notifications'

import {
  CourtReporterExhibit,
  CourtReporterExhibitSubmission,
  ExhibitInclusion,
  ExhibitRecordDesignation,
} from '@/apiClient'
import { fileExtension } from '@/utils/file'

// The file types Parrot takes as an exhibit (`INTERNAL_EXHIBIT_MIME_TYPES`).
export const EXHIBIT_MIME_TYPES = [
  'text/csv',
  'text/plain',
  'application/rtf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.oasis.opendocument.text',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.oasis.opendocument.spreadsheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.oasis.opendocument.presentation',
  'application/pdf',
  'image/bmp',
  'image/gif',
  'image/tiff',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/heic',
  'image/heif',
  'audio/aac',
  'audio/x-m4a',
  'audio/wav',
  'audio/x-wav',
  'audio/mp3',
  'audio/x-ms-wma',
  'audio/mp4',
  'audio/mpeg',
  'video/msvideo',
  'video/x-msvideo',
  'video/x-ms-asf',
  'video/quicktime',
  'video/avi',
  'video/vob',
  'video/x-ms-wma',
  'video/x-ms-wmv',
  'video/mp4',
  'video/mpeg',
  'video/3gpp',
  'application/x-7z-compressed',
  'application/zip',
  'message/rfc822',
]

// Browsers leave a file's type empty or use an alias (a Windows ZIP, a HEIC photo), so the
// extension names the type Parrot takes when the browser's doesn't.
const EXHIBIT_EXTENSION_MIME_TYPES: Record<string, string> = {
  csv: 'text/csv',
  txt: 'text/plain',
  rtf: 'application/rtf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  odt: 'application/vnd.oasis.opendocument.text',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ods: 'application/vnd.oasis.opendocument.spreadsheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  odp: 'application/vnd.oasis.opendocument.presentation',
  pdf: 'application/pdf',
  bmp: 'image/bmp',
  gif: 'image/gif',
  tif: 'image/tiff',
  tiff: 'image/tiff',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  heic: 'image/heic',
  heif: 'image/heif',
  aac: 'audio/aac',
  m4a: 'audio/x-m4a',
  wav: 'audio/wav',
  mp3: 'audio/mpeg',
  wma: 'audio/x-ms-wma',
  avi: 'video/x-msvideo',
  asf: 'video/x-ms-asf',
  mov: 'video/quicktime',
  vob: 'video/vob',
  wmv: 'video/x-ms-wmv',
  mp4: 'video/mp4',
  mpeg: 'video/mpeg',
  mpg: 'video/mpeg',
  '3gp': 'video/3gpp',
  '7z': 'application/x-7z-compressed',
  zip: 'application/zip',
  eml: 'message/rfc822',
}

export const EXHIBIT_FILE_ACCEPT = [
  ...EXHIBIT_MIME_TYPES,
  ...Object.keys(EXHIBIT_EXTENSION_MIME_TYPES).map(ext => `.${ext}`),
].join(',')

export const exhibitMimeType = (file: File): string | null => {
  if (EXHIBIT_MIME_TYPES.includes(file.type)) {
    return file.type
  }
  return EXHIBIT_EXTENSION_MIME_TYPES[fileExtension(file.name)] ?? null
}

// The API creates at most this many exhibits in one request.
export const MAX_EXHIBITS_PER_SAVE = 100

export const DESIGNATION_LABELS: Record<ExhibitRecordDesignation, string> = {
  unknown: 'Unknown',
  plaintiff: 'Plaintiff',
  defendant: 'Defendant',
  joint: 'Joint',
  court: 'Court',
  witness_attorney: 'Witness attorney',
  other: 'Other',
}

export const INCLUSION_LABELS: Record<ExhibitInclusion, string> = {
  unknown: 'Unknown',
  declared: 'Declared',
  extra: 'Extra',
}

export const DESIGNATION_OPTIONS = [
  ExhibitRecordDesignation.plaintiff,
  ExhibitRecordDesignation.defendant,
].map(value => ({ value, label: DESIGNATION_LABELS[value] }))

export const INCLUSION_OPTIONS = [
  ExhibitInclusion.declared,
  ExhibitInclusion.extra,
].map(value => ({ value, label: INCLUSION_LABELS[value] }))

export const isDesignated = (designation: ExhibitRecordDesignation | null) =>
  designation !== null && designation !== ExhibitRecordDesignation.unknown

export const isAcceptedExhibitFile = (file: File) =>
  exhibitMimeType(file) !== null

export const notifyUnsupported = (file: File) =>
  showNotification({
    color: 'red',
    message: `${file.name}: this file type is not supported.`,
  })

export type ExhibitSubmissionState = 'open' | 'submitted' | 'closed' | 'missed'

export const exhibitSubmissionState = (
  submission: CourtReporterExhibitSubmission,
): ExhibitSubmissionState => {
  if (submission.submitted_at) {
    return 'submitted'
  }
  if (submission.closed_at) {
    return 'closed'
  }
  return submission.can_submit ? 'open' : 'missed'
}

export const ownExhibits = (submission: CourtReporterExhibitSubmission) =>
  submission.exhibits.filter(e => e.editable)

// A missed window leaves the CR's unsubmitted exhibits out of the event, as Parrot does.
export const shownExhibits = (
  submission: CourtReporterExhibitSubmission,
  state: ExhibitSubmissionState,
) =>
  state === 'missed'
    ? submission.exhibits.filter(e => !e.is_own)
    : submission.exhibits

// Unlike `editable`, this still holds once the exhibits are submitted.
export const hasCourtReporterExhibits = (
  submission: CourtReporterExhibitSubmission,
) => submission.exhibits.some(e => e.is_own)

export const submitBlocker = (
  submission: CourtReporterExhibitSubmission,
): string | null => {
  const own = ownExhibits(submission)
  if (own.some((e: CourtReporterExhibit) => !isDesignated(e.designation))) {
    return 'Set a designation for every exhibit before submitting.'
  }
  return null
}
